import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { w3cwebsocket as W3CWebSocket } from 'websocket';
import AppService from '../../../../services/app-service';
import SecurityService from '../../../../services/security-service';
import AuthService from '../../../../services/authentication-service';
import { ucsfInstance, ucsfLoginInstance } from '../../../../axios/ucsf';
import { COMPONENT_APIS } from '../../../../utils/omi-api';
import { CONST } from '../../../../utils/omi-constants';
import { trackLoginSuccess } from '../../../../utils/analytics';
import { setLoader, setUCSFWindow, setUserErrorData } from '../../../../../redux/app/app.actions';
import { getAppId, getUcsfWindow } from '../../../../../redux/app/app.selectors';
import { setCurrentUser } from '../../../../../redux/user/user.actions';
import './AuthCard.css';
import WelcomeFlow from './flows/WelcomeFlow';
import LoginFlow from './flows/LoginFlow';
import RegisterFlow from './flows/RegisterFlow';
import ResetPasswordFlow from './flows/ResetPasswordFlow';
import UserVerificationFlow from './flows/UserVerificationFlow';
import LoadingFlow from './flows/LoadingFlow';

const AuthCard = ({ onCollapse, history }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const appId = useSelector(getAppId);
  const ucsfWindow = useSelector(getUcsfWindow);
  const [view, setView] = useState('welcome');

  // Extract URL parameters for token-based flows
  const urlParams = AppService.fetchQueryParams(location.search);

  // Listen for the Shibboleth token sent back after the UCSF popup completes.
  useEffect(() => {
    const logoutID = SecurityService.getLogoutId();
    if (appId && !logoutID) {
      const endPoint = `${CONST.WS_POINT}/ws/login/${appId}/`;
      const WS_EXT = { 'Sec-WebSocket-Extensions': 'permessage-deflate; client_max_window_bits' };
      const ws = new W3CWebSocket(endPoint, '', '', WS_EXT);
      ws.onmessage = (evt) => {
        const userData = evt?.data ? JSON.parse(evt.data) : null;
        if (
          userData &&
          userData.token &&
          Number(userData?.status_code) === 200 &&
          userData?.appid === appId
        ) {
          SecurityService.setSecurityToken(userData.token);
          AuthService._setUser(userData);
          setTimeout(() => {
            if (ucsfWindow) { ucsfWindow.close(); ws.close(); dispatch(setUCSFWindow(null)); }
            setTimeout(() => {
              ucsfInstance.get(COMPONENT_APIS.USER_PERMISSION).then(response => {
                const { permissions, role } = response.data;
                AuthService._setUser({ ...userData, permissions, role });
                dispatch(setCurrentUser({ ...userData, permissions, role }));
                const userId = userData?.user?.id;
                trackLoginSuccess(userId, 'oldham');
                dispatch(setLoader(false));
                history.push('/home');
              }).catch(() => {
                const userId = userData?.user?.id;
                trackLoginSuccess(userId, 'oldham');
                dispatch(setLoader(false));
                history.push('/home');
              });
            }, 500);
          }, 250);
        } else if (userData && Number(userData?.status_code) !== 200) {
          dispatch(setUserErrorData(userData));
          setTimeout(() => {
            if (ucsfWindow) ucsfWindow.close();
            history.push('/');
          }, 500);
        }
      };
      return () => { ws.close(); };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, ucsfWindow]);

  const handleOldhamLogin = () => {
    dispatch(setUserErrorData(null));
    AppService.clearAllCookies();
    localStorage.clear();
    dispatch(setLoader(true));
    ucsfInstance.get(COMPONENT_APIS.VERSION_CHECK).then(() => {
      dispatch(setLoader(false));
      const UCSF_LOGIN_LINK = `${CONST.END_POINT}/secure/appid=${appId}`;
      const ucsf = window.open(UCSF_LOGIN_LINK, `ucsf_${appId}`, 'toolbar=yes, top=200, left=500, width=650, height=700');
      dispatch(setUCSFWindow(ucsf));
    }).catch(() => {
      dispatch(setLoader(false));
    });
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const handleGuestLogin = () => {
    dispatch(setLoader(true, 'Logging in as Guest User'));
    const username = 'guestuser';
    const password = 'omicon@123';
    ucsfLoginInstance.post(COMPONENT_APIS.USER_LOGIN, { username, password }).then((response) => {
      const userData = response.data;
      const userId = userData?.user?.id;
      trackLoginSuccess(userId, 'guest');
      SecurityService.setSecurityToken(userData.token);
      AuthService._setUser(userData);
      ucsfInstance.get(COMPONENT_APIS.USER_PERMISSION).then(res => {
        const { permissions, role } = res.data;
        AuthService._setUser({ ...userData, permissions, role });
        dispatch(setCurrentUser({ ...userData, permissions, role }));
        dispatch(setLoader(false));
        history.push('/home');
      }).catch(() => {
        dispatch(setLoader(false));
      });
    }).catch(() => {
      dispatch(setLoader(false));
      AppService.setNotification({
        title: 'Error',
        message: 'Something went wrong. Please try again.',
        type: 'danger'
      });
    });
  };

  const handleCollapse = () => {
    setView('welcome');
    onCollapse();
  };

  const renderFlow = () => {
    switch (view) {
      case 'welcome':
        return <WelcomeFlow onViewChange={handleViewChange} onGuestLogin={handleGuestLogin} onOldhamLogin={handleOldhamLogin} />;
      case 'login':
        return <LoginFlow onViewChange={handleViewChange} history={history} />;
      case 'register':
        return <RegisterFlow onViewChange={handleViewChange} />;
      case 'resetPassword':
        return <ResetPasswordFlow onViewChange={handleViewChange} urlParams={urlParams} />;
      case 'userVerification':
        return <UserVerificationFlow onViewChange={handleViewChange} urlParams={urlParams} />;
      case 'loading':
        return <LoadingFlow />;
      default:
        return <WelcomeFlow onViewChange={handleViewChange} onGuestLogin={handleGuestLogin} onOldhamLogin={handleOldhamLogin} />;
    }
  };

  return (
    <div className={`auth-card-wrapper ${view}`}>
      <div className="auth-card">
        <div className="auth-card-scroll-container">
          {renderFlow()}
        </div>
      </div>

      <button
        className="auth-card-collapse-button"
        onClick={handleCollapse}
        title="Collapse"
        aria-label="Collapse auth options"
      >
        <img src="/assets/icons/arrow-up.svg" alt="Collapse" className='collapse-icon'/>
      </button>
    </div>
  );
};

export default AuthCard;
